import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/utils";
import { RootState } from "@/store/store";
import FiltersSheet from "./search/FiltersSheet";
import SearchHighlight from "./search/SearchHighlight";
import SuggestionList, { optionId, RangeCalendar } from "./search/SuggestionList";
import { useLongPress } from "./search/useLongPress";
import { useSearchAutocomplete } from "./search/useSearchAutocomplete";

interface BorrowersSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const DEBOUNCE_MS = 300;
const POPOVER_WIDTH_PX = 300;
/** Shared by the input and its highlight copy so every glyph lines up; 16px on phones avoids iOS zoom. */
const BAR_TEXT = "h-10 border pl-10 pr-3 text-base sm:text-sm";

const BorrowersSearch: React.FC<BorrowersSearchProps> = ({
  value,
  onChange,
  placeholder = "Search",
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [sheetOpen, setSheetOpen] = useState(false);
  const sentRef = useRef(value);
  const debounceRef = useRef<number>();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const mirrorRef = useRef<HTMLDivElement>(null);
  const markerRef = useRef<HTMLSpanElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const coarse = useMediaQuery("(pointer: coarse)");
  const role = useSelector((state: RootState) => state.auth.user?.role);
  const organizationCount = useSelector(
    (state: RootState) => state.auth.user?.organizations?.length ?? 0
  );
  const viewer = useMemo(() => ({ role, organizationCount }), [role, organizationCount]);

  // A changed `value` that is not our own send (the Status select) replaces the text.
  useEffect(() => {
    if (value === sentRef.current) return;
    sentRef.current = value;
    setLocalValue(value);
  }, [value]);

  const send = useCallback(
    (next: string) => {
      window.clearTimeout(debounceRef.current);
      sentRef.current = next;
      onChange(next);
    },
    [onChange]
  );

  const autocomplete = useSearchAutocomplete({
    raw: localValue,
    onRawChange: setLocalValue,
    onSubmit: send,
    viewer,
    coarse,
    inputRef,
  });
  const { committedRaw, open, suggestions, activeIndex, calendarOpen, hasMore, context } =
    autocomplete;
  // While the popover animates out it keeps what it showed while open: after an accept
  // the text already calls for another list (an exact value shows them all).
  const source = context?.kind === "value" ? context.filter.options : undefined;
  const shownRef = useRef({ suggestions, activeIndex, calendarOpen, source, hasMore });
  if (open) shownRef.current = { suggestions, activeIndex, calendarOpen, source, hasMore };
  const shown = shownRef.current;

  useEffect(() => {
    if (committedRaw === sentRef.current) return;
    debounceRef.current = window.setTimeout(() => send(committedRaw), DEBOUNCE_MS);
    return () => window.clearTimeout(debounceRef.current);
  }, [committedRaw, send]);

  const syncScroll = useCallback(() => {
    const input = inputRef.current;
    mirrorRef.current?.style.setProperty("--search-scroll", `${input?.scrollLeft ?? 0}px`);
  }, []);
  // WebKit can scroll the input after the event that moved the caret.
  const syncScrollNextFrame = useCallback(() => requestAnimationFrame(syncScroll), [syncScroll]);

  // Keeps the highlight scrolled with the input after each change, and the open
  // popover anchored under the token being edited.
  useLayoutEffect(() => {
    syncScroll();
    const anchor = anchorRef.current;
    const wrapper = wrapperRef.current;
    const marker = markerRef.current;
    if (!open || !anchor || !wrapper) return;
    if (coarse || !marker) {
      anchor.style.left = "0px";
      anchor.style.width = "100%";
      return;
    }
    const left = marker.getBoundingClientRect().left - wrapper.getBoundingClientRect().left;
    const maxLeft = Math.max(0, wrapper.clientWidth - POPOVER_WIDTH_PX);
    anchor.style.left = `${Math.min(Math.max(left, 0), maxLeft)}px`;
    anchor.style.width = "0px";
  });

  const longPress = useLongPress(() => setSheetOpen(true));
  const focusInput = () => {
    if (longPress.consumeLongPress()) return;
    const input = inputRef.current;
    input?.focus();
    input?.setSelectionRange(input.value.length, input.value.length);
  };

  return (
    <Popover open={open} onOpenChange={(next) => !next && autocomplete.dismiss()}>
      <div ref={wrapperRef} className="relative">
        <PopoverAnchor asChild>
          <div ref={anchorRef} aria-hidden className="pointer-events-none absolute inset-y-0" />
        </PopoverAnchor>
        <Search className="pointer-events-none absolute left-3.5 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
        <div
          ref={mirrorRef}
          aria-hidden
          className={cn(
            BAR_TEXT,
            "pointer-events-none absolute inset-0 flex items-center rounded-md border-transparent bg-white"
          )}
        >
          <div className="min-w-0 flex-1 overflow-hidden">
            <span className="block whitespace-pre [transform:translateX(calc(var(--search-scroll,0px)*-1))]">
              <SearchHighlight
                raw={localValue}
                tokens={autocomplete.tokens}
                anchorIndex={autocomplete.anchorIndex}
                markerRef={markerRef}
              />
            </span>
          </div>
        </div>
        <Input
          ref={inputRef}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={open && !calendarOpen ? listId : undefined}
          aria-activedescendant={activeIndex >= 0 ? optionId(listId, activeIndex) : undefined}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          className={cn(
            BAR_TEXT,
            "relative bg-transparent text-transparent caret-foreground shadow-sm selection:bg-primary/25 selection:text-transparent placeholder:text-muted-foreground/50"
          )}
          placeholder={placeholder}
          value={localValue}
          onScroll={syncScroll}
          onKeyUp={syncScrollNextFrame}
          {...autocomplete.inputProps}
          onSelect={(event) => {
            autocomplete.inputProps.onSelect(event);
            syncScrollNextFrame();
          }}
        />
        {coarse && !autocomplete.focused && (
          // Over the idle bar on touch: a tap focuses the input, press-and-hold opens the
          // filters sheet without the keyboard, iOS text selection or callout getting in.
          <div
            aria-hidden
            className="absolute inset-0 z-20 touch-manipulation select-none [-webkit-touch-callout:none]"
            onClick={focusInput}
            {...longPress.handlers}
          />
        )}
      </div>
      <PopoverContent
        align="start"
        sideOffset={4}
        updatePositionStrategy="always"
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onInteractOutside={(event) => {
          if (wrapperRef.current?.contains(event.target as Node)) event.preventDefault();
        }}
        // Focus stays in the input, also when the calendar is clicked.
        onMouseDown={(event) => event.preventDefault()}
        className={cn(
          coarse ? "w-[var(--radix-popover-trigger-width)] p-0" : "w-[300px] p-1",
          shown.calendarOpen && "w-auto p-0"
        )}
      >
        {shown.calendarOpen ? (
          <RangeCalendar onPick={autocomplete.acceptRange} />
        ) : (
          <SuggestionList
            id={listId}
            suggestions={shown.suggestions}
            activeIndex={shown.activeIndex}
            source={shown.source}
            hasMore={shown.hasMore}
            coarse={coarse}
            onAccept={autocomplete.accept}
          />
        )}
      </PopoverContent>
      <span className="sr-only" aria-live="polite">
        {/* The count is options only; the aria-hidden footer's hint is said in words. */}
        {open && !calendarOpen
          ? `${suggestions.length} suggestion${suggestions.length === 1 ? "" : "s"}${
              hasMore ? ", type to narrow" : ""
            }`
          : ""}
      </span>
      <FiltersSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        raw={localValue}
        tokens={autocomplete.tokens}
        onRawChange={setLocalValue}
        viewer={viewer}
      />
    </Popover>
  );
};

export default BorrowersSearch;
